// /social-security-work-cert — ประกันสังคม / หนังสือรับรองการทำงาน (work certification)
import { createFileRoute } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const TITLE = "หนังสือรับรองการทำงาน + ประกันสังคม — แปล/รับรอง สถานทูต | NYC";
export const DESC = "บริการคัด แปล รับรองหนังสือรับรองการทำงาน (Certificate of Employment) และเอกสารประกันสังคม (สปส.6-09 — สอบถามฟรี ไม่มีข้อผูกมัด ทักแชทได้ทันที";

export const steps = [
  { name: "เตรียม/คัดเอกสาร", text: "หนังสือรับรองการทำงานจากนายจ้าง (ตัวจริง ไม่เกิน 3 เดือน), สลิปเงินเดือน 3-6 เดือน, สปส.6-09 (ขึ้นทะเบียนผู้ประกันตน), สปส.1-03 (เปลี่ยนแปลงข้อเท็จจริง) — NYC ช่วยประสานนายจ้าง/สปส. หากจำเป็น" },
  { name: "ขอเอกสารจากสำนักงานประกันสังคม", text: "ยื่นแบบฟอร์มขอใบรับรองการเป็นผู้ประกันตน + ประวัติการนำส่งเงินสมทบ ที่ สปส. เขตที่นายจ้างขึ้นทะเบียน (1-3 วัน)" },
  { name: "แปลโดยนักแปลขึ้นทะเบียน", text: "แปลเป็นภาษาที่สถานทูต/นายจ้างต่างประเทศยอมรับ พร้อมตราประทับนักแปลรับรอง MFA" },
  { name: "Notary + รับรอง MFA / Apostille", text: "Notary Public ยืนยันลายเซ็นนายจ้าง + ยื่น MFA แจ้งวัฒนะ (Apostille หรือ Consular)" },
  { name: "ยื่นสถานทูตปลายทาง", text: "สำหรับขอวีซ่าทำงาน, PR, สัญชาติ ที่ต้องแสดงประวัติการทำงานในไทย" },
];

export const faqs = [
  { q: "ทำไมต้องใช้เอกสารประกันสังคม?", a: "หลายประเทศ (ออสเตรเลีย แคนาดา เยอรมัน ญี่ปุ่น) ต้องการหลักฐานยืนยันการจ้างงานจริงในไทยจากระบบประกันสังคม (Social Security record) ซึ่งเป็นหลักฐานทางการที่ปลอมแปลงยากกว่า slip เงินเดือน" },
  { q: "สปส.6-09 คืออะไร?", a: "หนังสือรับรองการเป็นผู้ประกันตน ออกโดยสำนักงานประกันสังคม ระบุชื่อนายจ้าง วันเริ่ม-สิ้นสุดการทำงาน และจำนวนเงินสมทบ — ใช้ยืนยันประวัติการทำงานต่อหน่วยงานต่างประเทศ" },
  { q: "ขอเองได้ไหม?", a: "ได้ — ผู้ประกันตนยื่นที่สำนักงานประกันสังคมเขต (สปส.) ทุกแห่งทั่วประเทศ พร้อมบัตรประชาชน ฟรีค่าธรรมเนียมตาม พ.ร.บ.ประกันสังคม 2533 มาตรา 33 — หรือมอบอำนาจให้ NYC ดำเนินการแทน (หนังสือมอบอำนาจติดอากร 30 บาท) ลดเวลาเดินทาง" },
  { q: "ใช้เวลาเท่าไร?", a: "สปส. ออกเอกสาร 1–3 วันทำการ (สำเนา สปส.6-09/6-10) + แปลรับรอง 1–2 วัน + MFA กรมการกงสุล แจ้งวัฒนะ 2 วัน (Express 1 วัน +800฿) + สถานทูตปลายทาง 3–5 วัน รวมประมาณ 7–12 วันทำการ" },
  { q: "ใช้สำหรับวีซ่า PR ออสเตรเลีย/แคนาดา ได้ไหม?", a: "ใช้ได้ — เป็นหลักฐาน Skilled Work Experience ตามเกณฑ์ Subclass 189/190/491 (AU, DHA Form 80 ส่วน E) และ Express Entry (CA, IRCC ระบบ CRS) แปลโดยนักแปล NAATI CPN9X0Q4P สำหรับ AU และ ATIO/OTTIAQ สำหรับ CA" },
  { q: "นายจ้างเก่าไม่ออกเอกสารให้ ทำอย่างไร?", a: "ใช้ชุดเอกสารทดแทนตามเกณฑ์ DHA/IRCC: (1) สปส.6-09 แสดงประวัตินายจ้าง (2) ภ.ง.ด.91 5 ปีย้อนหลังจากกรมสรรพากร (3) Bank statement เงินเดือนเข้า (4) Reference letter จากเพื่อนร่วมงาน + LinkedIn — ทีม NYC ช่วยจัดชุด Statutory Declaration" },
  { q: "ค่าบริการเริ่มต้นเท่าไร?", a: "เริ่มต้น 3,500 บาท (คัด สปส.6-09/6-10 + แปลอังกฤษ + MFA กรมการกงสุล) — เพิ่มสถานทูตแยกตามประเทศ: ออสเตรเลีย 0฿ (NAATI พอ), แคนาดา 2,500฿, อังกฤษ 4,500฿, เยอรมัน 3,800฿, ญี่ปุ่น 2,800฿ — Express +50%" },
  { q: "ต้องเป็นภาษาอังกฤษเท่านั้นไหม?", a: "ไม่ — แปลได้ทุกภาษาหลักตามที่สถานทูตปลายทางกำหนด เช่น เยอรมัน (Beglaubigte Übersetzung §142 ZPO), ญี่ปุ่น (在留資格), เกาหลี (출입국관리법), ฝรั่งเศส (Traducteur Assermenté), สเปน (Traductor Jurado MAEC) — รวม 22+ ภาษา" },

];

export const Route = createFileRoute("/social-security-work-cert/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/social-security-work-cert", "th")}, { name: "description", content: ctaDesc(DESC, "/social-security-work-cert", "th")},
      { name: "keywords", content: "หนังสือรับรองการทำงาน, ประกันสังคม, สปส.6-09, Certificate of Employment, Social Security Thailand, แปลใบรับรองทำงาน, ยื่นวีซ่า PR" },
      { property: "og:title", content: ctaTitle(TITLE, "/social-security-work-cert", "th")}, { property: "og:description", content: ctaDesc(DESC, "/social-security-work-cert", "th")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/social-security-work-cert" },
    ],
    links: canonicalAndHreflang("/social-security-work-cert", "/en/social-security-work-cert"),
    scripts: [
      ...buildLandingSchemas({
        path: "/social-security-work-cert", title: TITLE, description: DESC, serviceType: "Employment & Social Security Document Legalization", priceFromThb: 3500,
        steps, faqs,
        breadcrumbs: [{ name: "Home", path: "/" }, { name: "บริการ", path: "/services" }, { name: "หนังสือรับรองการทำงาน", path: "/social-security-work-cert" }],
      }),
      entitySameAsSchema(),
      softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing employment + social security certification tooling." }),
      claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(`${SITE}/social-security-work-cert`)),
    ],
  }),
  component: () => (
    <ServiceLanding
      quickAnswer={{
        q: "หนังสือรับรองการทำงาน + ประกันสังคม สำหรับยื่นวีซ่า/PR ทำอย่างไร?",
        a: "NYC คัด สปส.6-09 / สปส.1-03 ภายใน 1–3 วัน แปลโดยนักแปลขึ้นทะเบียน MFA ทำ Notary ลายเซ็นนายจ้าง รับรอง MFA/Apostille และยื่นสถานทูตปลายทาง (AU/CA/DE/JP/NZ) ครบใน 7–12 วันทำการ เริ่ม 3,500 บาท — DBD 0435567000061.",
      }}
      badge="ประกันสังคม · หนังสือรับรองการทำงาน"
      title="หนังสือรับรองการทำงาน + ประกันสังคม ครบวงจร"
      subtitle={DESC}
      heroBullets={[
        "คัด สปส.6-09 / สปส.1-03 ภายใน 1-3 วัน",
        "แปล + รับรองนักแปล MFA Thailand",
        "Notary ลายเซ็นนายจ้าง + Apostille / Consular",
        "ยื่นสถานทูตปลายทาง พร้อมส่ง DHL ทั่วโลก",
      ]}
      whoFor={{
        th: ["ผู้ขอ Skilled Migration Australia/Canada", "ผู้ขอวีซ่าทำงานเยอรมัน/ญี่ปุ่น", "ผู้สมัครเรียน MBA ที่ต้องแสดง work experience"],
        foreign: ["Foreign workers in Thailand needing employment proof for home-country immigration", "Expats returning home with Thai work history"],
      }}
      steps={steps}
      pricing={[
        { label: "คัด สปส.6-09 (มอบอำนาจ)", price: "1,200", note: "+ ค่าธรรมเนียมราชการ" },
        { label: "แปล + รับรอง MFA", price: "1,500/หน้า", note: "อังกฤษ" },
        { label: "Notary ลายเซ็นนายจ้าง", price: "เริ่มต้น 1,500", note: "ที่ออฟฟิศหรือไป on-site" },
        { label: "แพ็กเกจครบ Skilled Migration", price: "เริ่มต้น 5,500", note: "AU/CA/NZ" },
      ]}
      faqs={faqs}
      relatedLinks={[
        { to: "/translate/mfa", label: "แปล + รับรอง MFA" },
        { to: "/translate/naati", label: "NAATI ออสเตรเลีย" },
        { to: "/notary", label: "Notary Public" },
        { to: "/visa", label: "วีซ่าทุกประเทศ" },
        { to: "/embassy", label: "Embassy Legalization" },
        { to: "/blog/social-security-employment-certificate-skilled-migration", label: "บทความ: ประกันสังคม + Skilled Migration" },
      ]}
    />
  ),
});
